import { useState, useRef } from "react";
import { Eye, IdCard, ScanFace, ShieldCheck, AlertTriangle, CheckCircle2, Upload, Loader2, X, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TABS = [
  { k: "ocr", l: "OCR Patente/CI", i: IdCard, color: "sky" },
  { k: "live", l: "Liveness Check", i: ScanFace, color: "fuchsia" },
  { k: "scan", l: "Damage Scanner", i: Eye, color: "amber" },
];

const fileToBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export default function VisionHub() {
  const [tab, setTab] = useState("ocr");
  const [img, setImg] = useState(null);
  const [imgRef, setImgRef] = useState(null);
  const [carId, setCarId] = useState("");
  const [fase, setFase] = useState("uscita");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const fileRefRef = useRef(null);

  const pickImage = async (e, target) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Immagine max 8MB"); return; }
    const b64 = await fileToBase64(f);
    if (target === "ref") setImgRef(b64); else setImg(b64);
  };

  const reset = () => { setImg(null); setImgRef(null); setResult(null); };

  const run = async () => {
    if (!img) { toast.error("Carica prima un'immagine"); return; }
    setBusy(true); setResult(null);
    try {
      let endpoint, payload;
      if (tab === "ocr") {
        endpoint = "/kyc/ocr-patente";
        payload = { image_b64: img };
      } else if (tab === "live") {
        endpoint = "/kyc/liveness";
        payload = { image_b64: img, metadata: imgRef ? { reference_b64: imgRef } : {} };
      } else if (tab === "scan") {
        endpoint = "/scans/walkaround";
        payload = { image_b64: img, metadata: { car_id: carId || null, fase } };
      }
      const r = await api.post(endpoint, payload);
      setResult(r.data);
      toast.success("Analisi AI completata");
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="vision-hub-page">
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
          <Sparkles className="text-[#ff0033]" size={26} />
          AI Vision Hub
        </h1>
        <p className="text-sm text-white/45 mt-1">
          Computer Vision · OCR Patente · Liveness biometrico · Smart Check-in con scansione danni — tutto via GPT-4o Vision.
        </p>
      </div>

      {/* TABS */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); reset(); }} data-testid={`vh-tab-${t.k}`}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap border transition inline-flex items-center gap-2 ${
              tab === t.k ? "bg-[#ff0033]/15 border-[#ff0033]/50 text-white" : "bg-white/[0.03] border-white/10 text-white/55 hover:text-white"
            }`}>
            <t.i size={14} /> {t.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* UPLOAD COLUMN */}
        <div className="glass rounded-2xl p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            {tab === "ocr" ? "Carica patente/CI/passaporto" : tab === "live" ? "Carica selfie cliente" : "Carica foto veicolo"}
          </div>

          {/* Image preview / upload */}
          <div className="relative bg-white/[0.02] border border-dashed border-white/15 rounded-xl aspect-[4/3] grid place-items-center overflow-hidden">
            {img ? (
              <>
                <img src={img} alt="" className="w-full h-full object-contain" />
                <button onClick={() => setImg(null)} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full"><X size={14} /></button>
              </>
            ) : (
              <button onClick={() => fileRef.current?.click()} className="text-center py-8" data-testid="vh-upload-btn">
                <Upload size={32} className="mx-auto text-white/30 mb-2" />
                <div className="text-xs text-white/55">Click per caricare</div>
                <div className="text-[10px] text-white/30 mt-1">JPG/PNG max 8MB</div>
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e, "main")} data-testid="vh-file-input" />

          {/* Liveness reference (patente) */}
          {tab === "live" && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Foto di riferimento (es. patente — opzionale per matching)</div>
              <div className="relative bg-white/[0.02] border border-dashed border-white/15 rounded-xl aspect-[4/3] grid place-items-center overflow-hidden">
                {imgRef ? (
                  <>
                    <img src={imgRef} alt="" className="w-full h-full object-contain" />
                    <button onClick={() => setImgRef(null)} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full"><X size={14} /></button>
                  </>
                ) : (
                  <button onClick={() => fileRefRef.current?.click()} className="text-center py-6">
                    <Upload size={24} className="mx-auto text-white/30 mb-1" />
                    <div className="text-[11px] text-white/45">Carica foto patente per matching</div>
                  </button>
                )}
              </div>
              <input ref={fileRefRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e, "ref")} />
            </>
          )}

          {/* Scanner — car + fase */}
          {tab === "scan" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Car ID (opzionale)</label>
                <input value={carId} onChange={(e) => setCarId(e.target.value)} placeholder="UUID veicolo" data-testid="vh-car-id"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Fase</label>
                <select value={fase} onChange={(e) => setFase(e.target.value)} className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-xs">
                  <option value="uscita">Uscita (check-out)</option>
                  <option value="rientro">Rientro (check-in)</option>
                </select>
              </div>
            </div>
          )}

          <button onClick={run} disabled={!img || busy} data-testid="vh-analyze-btn"
            className="w-full btn-neon py-3 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {tab === "ocr" ? "Estrai dati documento" : tab === "live" ? "Verifica liveness" : "Analizza danni"}
          </button>
        </div>

        {/* RESULT COLUMN */}
        <div className="glass rounded-2xl p-5 space-y-3" data-testid="vh-result">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Risultato AI</div>
          {!result && !busy && <div className="text-center py-12 text-white/30 text-sm">Carica un'immagine e premi "Analizza"</div>}
          {busy && <div className="text-center py-12 text-white/55"><Loader2 size={24} className="animate-spin inline mb-2 text-[#ff0033]" /><div>Analisi AI in corso…</div></div>}
          {result && tab === "ocr" && <OCRResult data={result.data} />}
          {result && tab === "live" && <LivenessResult data={result.data} />}
          {result && tab === "scan" && <ScanResult data={result.data} delta={result.delta} />}
        </div>
      </div>
    </div>
  );
}

const OCRResult = ({ data }) => (
  <div className="space-y-2">
    <Pill ok={data.confidence > 0.7}>Confidence {((data.confidence || 0) * 100).toFixed(0)}%</Pill>
    {data.warnings && <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2"><AlertTriangle size={12} className="inline mr-1" /> {data.warnings}</div>}
    <div className="grid grid-cols-2 gap-2 text-xs">
      <Field label="Tipo">{data.tipo_documento}</Field>
      <Field label="Nome">{data.nome}</Field>
      <Field label="Cognome">{data.cognome}</Field>
      <Field label="Data nascita">{data.data_nascita}</Field>
      <Field label="N° Documento">{data.numero_documento}</Field>
      <Field label="Categoria">{data.categoria_patente}</Field>
      <Field label="Emissione">{data.data_emissione}</Field>
      <Field label="Scadenza">{data.data_scadenza}</Field>
      <Field label="Ente">{data.ente_emissione}</Field>
      <Field label="CF">{data.codice_fiscale}</Field>
    </div>
  </div>
);

const LivenessResult = ({ data }) => (
  <div className="space-y-3">
    <Pill ok={data.approved}>
      {data.approved ? <CheckCircle2 size={12} className="inline mr-1" /> : <AlertTriangle size={12} className="inline mr-1" />}
      {data.approved ? "APPROVATO" : "BLOCCATO"}
    </Pill>
    <Bar label="Liveness Score" value={data.liveness_score} />
    <Bar label="Qualità foto" value={data.quality_score} />
    {data.match_score != null && <Bar label="Match con riferimento" value={data.match_score} />}
    {data.anomalies?.length > 0 && (
      <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">
        <div className="text-[10px] uppercase font-bold text-rose-300 mb-1">Anomalie rilevate</div>
        <ul className="text-xs text-rose-200 list-disc ml-4">{data.anomalies.map((a, i) => <li key={i}>{a}</li>)}</ul>
      </div>
    )}
    {data.reason && <div className="text-xs text-white/55 italic">"{data.reason}"</div>}
  </div>
);

const ScanResult = ({ data, delta }) => (
  <div className="space-y-3">
    <div className="flex gap-2 flex-wrap">
      <Pill ok={data.veicolo_riconosciuto}>{data.veicolo_riconosciuto ? "Veicolo OK" : "Veicolo non identificato"}</Pill>
      {data.colore_rilevato && <Pill>Colore: {data.colore_rilevato}</Pill>}
      <Pill ok>Qualità foto {data.foto_qualita}%</Pill>
    </div>
    <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3">
      <div className="text-[10px] uppercase font-bold text-amber-300 mb-1">Danni rilevati: {(data.danni || []).length}</div>
      <div className="text-2xl font-black text-amber-200">€ {data.totale_stimato_eur || 0}</div>
    </div>
    {(data.danni || []).length > 0 && (
      <div className="space-y-1">
        {data.danni.map((d, i) => (
          <div key={i} className="bg-white/[0.03] border border-white/10 rounded-lg p-2 flex justify-between items-start text-xs">
            <div>
              <div className="font-semibold">{d.posizione}</div>
              <div className="text-[10px] text-white/55">{d.tipo} · {d.gravita}</div>
              {d.note && <div className="text-[10px] text-white/40 italic mt-0.5">{d.note}</div>}
            </div>
            <div className="font-bold text-amber-300 whitespace-nowrap">€{d.costo_stimato_eur}</div>
          </div>
        ))}
      </div>
    )}
    {delta && delta.nuovi_danni?.length > 0 && (
      <div className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3">
        <div className="text-[10px] uppercase font-bold text-rose-300 mb-2">🚨 Nuovi danni vs uscita: € {delta.totale_nuovi_eur}</div>
        {delta.nuovi_danni.map((d, i) => (
          <div key={i} className="text-xs text-rose-200">• {d.posizione} — {d.tipo} (€{d.costo_stimato_eur})</div>
        ))}
      </div>
    )}
  </div>
);

const Field = ({ label, children }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-lg p-2">
    <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="text-white font-mono">{children || <span className="text-white/30">—</span>}</div>
  </div>
);

const Pill = ({ ok, children }) => (
  <span className={`inline-block text-[10px] uppercase font-bold px-2 py-1 rounded border ${ok ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`}>
    {children}
  </span>
);

const Bar = ({ label, value = 0 }) => {
  const v = Math.max(0, Math.min(100, value));
  const color = v >= 75 ? "from-emerald-500 to-emerald-400" : v >= 50 ? "from-amber-500 to-amber-400" : "from-rose-500 to-rose-400";
  return (
    <div>
      <div className="flex justify-between text-[10px] mb-1">
        <span className="uppercase tracking-widest text-white/45">{label}</span>
        <span className="font-mono font-bold text-white">{v}/100</span>
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};
